import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import AppColors from 'general/constants/AppColors';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import DialogEditEnergyAccountView from '../DialogEditEnergyAccountView';

ChargeStationEnergyAccountListView.propTypes = {
  energyAccountList: PropTypes.array,
  hasAppTab: PropTypes.bool,
  onRefresh: PropTypes.func,
};

function ChargeStationEnergyAccountListView({
  energyAccountList = [],
  hasAppTab = false,
  onRefresh,
}) {
  // MARK: ---- PROPERTIES ----
  const user = useSelector((state) => state.auth.user);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'OPERATOR';
  const isInvestor = user?.role === 'AGENCY' && user?.agency?.primaryRole === 'INVESTOR';

  // MARK: ---- METHODS ----
  const hasEnergyAccountInfo = (item) =>
    Utils.isValid(item?.evnCustomerId) ||
    Utils.isValid(item?.username) ||
    Utils.isValid(item?.password);

  const validEnergyAccountList = energyAccountList.filter(hasEnergyAccountInfo);

  const buildEnergyAccountCardItem = (item, number) => {
    const color =
      item?.status === ChargeConstants.EnergyAccountStatus.VALIDATED
        ? AppColors.primary
        : item?.status === ChargeConstants.EnergyAccountStatus.PENDING
          ? AppColors.danger
          : 'white';
    const actions = [];
    if (item?.status === ChargeConstants.EnergyAccountStatus.PENDING && isAdmin) {
      actions.push({
        text: 'Phê duyệt',
        iconElement: <i className="fa-regular fa-check-circle" />,
        click: () => {
          ChargeHelper.UI.showDialogConfirmApproveEnergyAccount(item, () => {
            if (onRefresh) {
              onRefresh();
            }
          });
        },
      });
    }
    if (isAdmin || isInvestor)
      actions.push({
        text: 'Chỉnh sửa',
        iconElement: <i className="fa-regular fa-edit" />,
        click: () => {
          AppCustomDialogHelper.show(
            'Tài khoản điện lực',
            <DialogEditEnergyAccountView
              item={item}
              onCancel={() => {
                AppCustomDialogHelper.hide();
              }}
              onDone={() => {
                AppCustomDialogHelper.hide();
                if (onRefresh) {
                  onRefresh();
                }
              }}
            />,
            'md',
            true,
            true
          );
        },
      });
    if (isAdmin)
      actions.push({
        text: 'Xoá tài khoản',
        iconElement: <i className="fa-regular fa-trash-can" />,
        click: () => {
          ChargeHelper.UI.showDialogConfirmDeleteEnergyAccount(item, () => {
            if (onRefresh) {
              onRefresh();
            }
          });
        },
      });

    return (
      <div>
        <div
          className="overflow-hidden position-relative"
          style={{
            border: `1px solid ${AppColors.gray[300]}`,
            borderRadius: '10px',
            backgroundColor: color,
          }}
        >
          <div
            className="position-absolute"
            style={{
              width: '100px',
              height: '100px',
              top: '-20px',
              right: '-20px',
              opacity: 0.1,
            }}
          >
            <i
              className={`w-100 h-100 fa-solid fa-transformer-bolt`}
              style={{
                color: 'rgba(64, 70, 116, 1)',
                paddingTop: '33px',
                paddingRight: '10px',
                fontSize: '6rem',
              }}
            />
          </div>

          <div
            style={{
              backgroundColor: 'white',
              borderBottomLeftRadius: '10px',
              borderBottomRightRadius: '10px',
              marginBottom: '5px',
            }}
          >
            {/* header */}
            <div
              className="px-5 pt-5 pb-2 d-flex align-items-start justify-content-between"
              style={{
                borderBottom: `1px dashed ${AppColors.gray[300]}`,
              }}
            >
              <div>
                <div className="d-flex align-items-center gap-2">
                  <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                    Tài khoản {number}
                  </span>
                  <KTLabel
                    text={ChargeHelper.Utils.getEnergyAccountStatusName(item?.status)}
                    isInline={true}
                    size={KTLabelSize.medium}
                    additionalClassName={`label-light-${ChargeHelper.Utils.getEnergyAccountStatusColor(
                      item?.status
                    )}`}
                  />
                </div>
                {/* ma tai khoan dien luc */}
                <div>
                  <span
                    style={UIHelper.buildFontStyle(
                      '700',
                      '38px',
                      item?.status === ChargeConstants.EnergyAccountStatus.VALIDATED
                        ? AppColors.primary
                        : AppColors.gray[800]
                    )}
                  >
                    {item?.evnCustomerId}
                  </span>
                </div>
              </div>
              <div>{UIHelper.buildMoreActionsButton(actions)}</div>
            </div>
            {/* body */}
            <div className="p-4">
              <div>
                <span style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[700])}>
                  Tài khoản:{' '}
                  <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                    {item?.username}
                  </span>
                </span>
              </div>
              <div className="mt-1">
                <span style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[700])}>
                  Mật khẩu:{' '}
                  <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                    {item?.password}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="px-4">
      {UIHelper.buildCard(
        validEnergyAccountList.length === 0 ? (
          UIHelper.buildEmptyNoData('Trạm sạc này chưa được khai báo tài khoản điện lực nào.')
        ) : (
          <div className="row m-0 p-0 px-2 pt-4">
            {validEnergyAccountList.map((item, index) => {
              return (
                <div
                  key={`ENERGY_ACCOUNT_${item?.energyAccountId ?? index}`}
                  className="col-lg-4 mb-4 px-2"
                >
                  {buildEnergyAccountCardItem(item, index + 1)}
                </div>
              );
            })}
          </div>
        ),
        false,
        false,
        true,
        false,
        AppColors.gray[300],
        hasAppTab
      )}
    </div>
  );
}

export default ChargeStationEnergyAccountListView;
